// components/Layout/PrintHeader.tsx
/**
 * Header block that only appears on the printed document (`print:block`).
 * Extracted from AuthenticatedApp (F3).
 */

import type { AcademicPlan, EnrolledCourse } from '../../types';
import { useCreditUnit } from '../../contexts/InstitutionConfigContext';

/**
 * Degree-credit target used for the printed progress line.
 *
 * W49 A3: `/api/institution/current` now exposes
 * `planning.graduation_credits` and App.tsx passes it in as
 * `graduationCredits`. This constant remains only as the prop default for
 * mounts outside the provider (tests).
 */
export const DEFAULT_GRADUATION_CREDITS = 120;

export interface PrintHeaderStudent {
  id?: string | number;
  name?: string;
  email?: string;
}

export interface PrintHeaderProps {
  /** Preferred identity (the synced DB user), falling back to `fallbackStudent`. */
  student?: PrintHeaderStudent | null;
  fallbackStudent: PrintHeaderStudent;
  academicPlan: AcademicPlan;
  /** Credits required to graduate at this institution. */
  graduationCredits?: number;
}

const PrintHeader = ({
  student,
  fallbackStudent,
  academicPlan,
  graduationCredits = DEFAULT_GRADUATION_CREDITS,
}: PrintHeaderProps) => {
  const creditUnit = useCreditUnit();
  const completedCourses = Object.values(academicPlan.semesters || {})
    .flat()
    .filter((course): course is EnrolledCourse => Boolean(course) && !course.isPlaceholder);

  const totalCredits = completedCourses.reduce((sum, course) => sum + (course.credits || 0), 0);
  const progressPercent = graduationCredits
    ? Math.round((totalCredits / graduationCredits) * 100)
    : 0;

  return (
    <div className="print-header hidden print:block">
      <h1>Academic Plan Summary</h1>
      <div className="print-header-info">
        <div>
          <div><strong>Student:</strong> {student?.name || fallbackStudent.name}</div>
          <div><strong>ID:</strong> {student?.id || fallbackStudent.id}</div>
          <div><strong>Email:</strong> {student?.email || fallbackStudent.email}</div>
          <div><strong>Major:</strong> {academicPlan.plan_name || 'Undeclared'}</div>
        </div>
        <div>
          <div><strong>Plan:</strong> {academicPlan.plan_name}</div>
          <div><strong>Total {creditUnit.heading}:</strong> {totalCredits} / {graduationCredits}</div>
          <div><strong>Overall Progress:</strong> {progressPercent}%</div>
          <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
        </div>
      </div>
    </div>
  );
};

export default PrintHeader;
